import { AchievementCategory, AchievementDefinition } from '@/models/Achievement';
import { ActivityLog } from '@/models/LearnTypes';
import { UserProfile } from '../ProfileService';

/**
 * Helper function to create XP milestone achievements
 */
const createXPAchievement = (
  xp: number, 
  tier: number, 
  title: string
): AchievementDefinition => {
  return {
    id: `xp-${xp}`,
    title,
    description: `Earned ${xp} XP in total`,
    icon: tier <= 3 ? '🏅' : tier <= 6 ? '🏆' : '👑',
    category: AchievementCategory.XP_MILESTONE,
    tier,
    xpReward: Math.ceil(xp * 0.05), // 5% of milestone as bonus
    condition: (user: UserProfile) => user.xp >= xp
  };
};

/**
 * Helper function to create perfect level achievements
 */
const createPerfectLevelAchievement = (
  count: number, 
  tier: number
): AchievementDefinition => {
  return {
    id: `perfect-level-${count}`,
    title: `Perfect Learner ${tier}`,
    description: `Complete ${count} perfect levels`,
    icon: '⭐',
    category: AchievementCategory.PERFECT_PERFORMANCE,
    tier,
    xpReward: count * 2,
    condition: (user: UserProfile, activities: ActivityLog[]) => {
      const perfectLevels = activities.filter(
        a => a.type === 'level-completed' && a.isPerfect === true
      );
      return perfectLevels.length >= count;
    }
  };
};

/**
 * Helper function to create perfect lesson achievements
 */
const createPerfectLessonAchievement = (
  count: number, 
  tier: number
): AchievementDefinition => {
  return {
    id: `perfect-lesson-${count}`,
    title: `Perfect Scholar ${tier}`,
    description: `Complete ${count} perfect lessons`,
    icon: '🌟',
    category: AchievementCategory.PERFECT_PERFORMANCE,
    tier,
    xpReward: count * 5,
    condition: (user: UserProfile, activities: ActivityLog[]) => {
      const perfectLessons = activities.filter(
        a => a.type === 'lesson-completed' && a.isPerfectLesson === true
      );
      return perfectLessons.length >= count;
    }
  };
};

/**
 * Calculate total XP from activities
 */
const calculateTotalXP = (activities: ActivityLog[]): number => {
  return activities.reduce((total, activity) => {
    return total + (activity.xpEarned || 0);
  }, 0);
};

/**
 * Group activities by date (YYYY-MM-DD)
 */
const groupActivitiesByDay = (activities: ActivityLog[]): Record<string, ActivityLog[]> => {
  const byDay: Record<string, ActivityLog[]> = {};
  
  activities.forEach(activity => {
    const date = new Date(activity.timestamp);
    const dateKey = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
    
    if (!byDay[dateKey]) {
      byDay[dateKey] = [];
    }
    
    byDay[dateKey].push(activity);
  });
  
  return byDay;
};

/**
 * Helper function to create daily level completion achievements
 */
const createDailyLevelsAchievement = (
  count: number, 
  tier: number
): AchievementDefinition => {
  return {
    id: `daily-levels-${count}`,
    title: `Daily Master ${tier}`,
    description: `Complete ${count} levels in a single day`,
    icon: '📚',
    category: AchievementCategory.DAILY_ACTIVITY,
    tier,
    xpReward: count,
    condition: (user: UserProfile, activities: ActivityLog[]) => {
      // Group by date and count levels completed per day
      const levelsByDay = groupActivitiesByDay(
        activities.filter(a => a.type === 'level-completed')
      );
      
      // Return true if any day has at least 'count' completions
      return Object.values(levelsByDay).some(dayActivities => dayActivities.length >= count);
    }
  };
};

/**
 * Helper function to create daily lesson completion achievements
 */
const createDailyLessonsAchievement = (
  count: number, 
  tier: number
): AchievementDefinition => {
  return {
    id: `daily-lessons-${count}`,
    title: `Day Champion ${tier}`,
    description: `Complete ${count} lessons in a single day`,
    icon: '📘',
    category: AchievementCategory.DAILY_ACTIVITY,
    tier,
    xpReward: count * 2,
    condition: (user: UserProfile, activities: ActivityLog[]) => {
      // Group by date and count lessons completed per day
      const lessonsByDay = groupActivitiesByDay(
        activities.filter(a => a.type === 'lesson-completed')
      );
      
      // Return true if any day has at least 'count' completions
      return Object.values(lessonsByDay).some(dayActivities => dayActivities.length >= count);
    }
  };
};

/**
 * Calculate the longest streak of consecutive days with lessons
 */
const calculateLongestStreak = (activities: ActivityLog[]): number => {
  // Filter to lesson completions and sort by date
  const lessonCompletions = activities
    .filter(a => a.type === 'lesson-completed')
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
  
  if (lessonCompletions.length === 0) return 0;
  
  // Get unique dates (YYYY-MM-DD)
  const uniqueDates = new Set<string>();
  lessonCompletions.forEach(activity => {
    const date = new Date(activity.timestamp);
    uniqueDates.add(`${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`);
  });
  
  // Convert to array of Date objects
  const dates = Array.from(uniqueDates)
    .map(dateStr => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    })
    .sort((a, b) => a.getTime() - b.getTime());
  
  let currentStreak = 1;
  let longestStreak = 1;
  
  // Calculate streak
  for (let i = 1; i < dates.length; i++) {
    const prevDate = dates[i - 1];
    const currentDate = dates[i];
    
    // Check if dates are consecutive
    const diffTime = currentDate.getTime() - prevDate.getTime();
    const diffDays = diffTime / (1000 * 60 * 60 * 24);
    
    if (diffDays === 1) {
      // Consecutive day
      currentStreak++;
      longestStreak = Math.max(longestStreak, currentStreak);
    } else if (diffDays > 1) {
      // Streak broken
      currentStreak = 1;
    }
  }
  
  return longestStreak;
};

/**
 * Helper function to create streak achievements
 */
const createStreakAchievement = (
  days: number, 
  tier: number
): AchievementDefinition => {
  const icons = ['🔥', '🔥🔥', '🔥🔥🔥', '🔥🔥🔥🔥', '🔥🔥🔥🔥🔥'];
  
  return {
    id: `streak-${days}`,
    title: `${days}-Day Streak`,
    description: `Complete lessons on ${days} consecutive days`,
    icon: icons[Math.min(tier - 1, icons.length - 1)],
    category: AchievementCategory.STREAK,
    tier,
    xpReward: days * 3,
    condition: (user: UserProfile, activities: ActivityLog[]) => {
      return calculateLongestStreak(activities) >= days;
    }
  };
};

// XP Milestone Achievements
const xpMilestones = [
  { xp: 100, tier: 1, title: 'XP Beginner' },
  { xp: 250, tier: 2, title: 'XP Apprentice' },
  { xp: 500, tier: 3, title: 'XP Enthusiast' },
  { xp: 1000, tier: 4, title: 'XP Explorer' },
  { xp: 1500, tier: 5, title: 'XP Adventurer' },
  { xp: 2000, tier: 6, title: 'XP Master' },
  { xp: 3000, tier: 7, title: 'XP Champion' },
  { xp: 4000, tier: 8, title: 'XP Legend' },
  { xp: 5000, tier: 9, title: 'XP Grandmaster' },
];

const xpAchievements = xpMilestones.map(({ xp, tier, title }) => 
  createXPAchievement(xp, tier, title)
);

// Perfect Level Achievements
const perfectLevelCounts = [1, 5, 10, 20, 50, 100];
const perfectLevelAchievements = perfectLevelCounts.map((count, index) => 
  createPerfectLevelAchievement(count, index + 1)
);

// Perfect Lesson Achievements
const perfectLessonCounts = [1, 5, 10, 20, 50, 100];
const perfectLessonAchievements = perfectLessonCounts.map((count, index) => 
  createPerfectLessonAchievement(count, index + 1)
);

// Daily Level Completion Achievements
const dailyLevelCounts = [5, 10, 25, 50];
const dailyLevelAchievements = dailyLevelCounts.map((count, index) => 
  createDailyLevelsAchievement(count, index + 1)
);

// Daily Lesson Completion Achievements
const dailyLessonCounts = [5, 10, 25, 50];
const dailyLessonAchievements = dailyLessonCounts.map((count, index) => 
  createDailyLessonsAchievement(count, index + 1)
);

// Streak Achievements
const streakDays = [3, 5, 7, 10, 20, 50, 100];
const streakAchievements = streakDays.map((days, index) => 
  createStreakAchievement(days, index + 1)
);

/**
 * Full list of achievement definitions
 */
export const achievements: AchievementDefinition[] = [
  ...xpAchievements,
  ...perfectLevelAchievements,
  ...perfectLessonAchievements,
  ...dailyLevelAchievements,
  ...dailyLessonAchievements,
  ...streakAchievements,
];

/**
 * Debug output of all achievements
 */
console.debug(`🏆 Registered ${achievements.length} achievements`);
achievements.forEach(achievement => {
  console.debug(`  - ${achievement.id}: ${achievement.title} (${achievement.category})`);
});
